import React, { useEffect, useState } from "react";
import "./style/geostyle.css";
import axios from "axios";
import Flag from "react-world-flags";

interface Country {
  name: string;
  code: string;
  continent: string;
}


interface TargetingData {
  geo: string; // Comma-separated string of country codes
  name: string;
  code: string;
}


interface ApiCountry {
  name: { common: string };
  cca2: string;
  region: string;
}

interface StepProps {
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  formData: {
    geo: string;
    name: string;
    code: string;
    [key: string]: string;
  };
  recordId: number;
  handleClose: () => void; // Close the popup
  onUpdate: () => void;  // Callback to refresh the data after update
}

const GeoTargetingPopup: React.FC<StepProps> = ({ handleInputChange, formData, recordId, handleClose, onUpdate }) => {
  const [availableCountries, setAvailableCountries] = useState<Country[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<Country[]>([]);
  const [continents, setContinents] = useState<string[]>([]);
  const [isContinentMode, setIsContinentMode] = useState<boolean>(false);
  const [enableRegionTargeting, setEnableRegionTargeting] = useState<boolean>(false);

    // Fetch data from API based on recordId
    useEffect(() => {
      const fetchCountries = async () => {
        try {
          // Fetch all countries first
          const response = await axios.get<ApiCountry[]>("https://restcountries.com/v3.1/all");
          const countries = response.data.map((country) => ({
            name: country.name.common,
            code: country.cca2,
            continent: country.region,
          }));
          setAvailableCountries(countries);
  
          // Extract unique continents
          const uniqueContinents = Array.from(new Set(countries.map((c) => c.continent)));
          setContinents(uniqueContinents);
  
          // Now, fetch specific data for this recordId to populate form fields
          const recordResponse = await axios.get<TargetingData>(`https://bidvertiser-api.vercel.app/api/targeting/geo/${recordId}`);
          const { geo, name, code } = recordResponse.data;
  
          // Populate selected countries from the fetched data
          const selectedCountryCodes = geo.split(","); // Assuming geo is stored as a comma-separated string of country codes
          const selectedCountryList = countries.filter((country) => selectedCountryCodes.includes(country.code));
          setSelectedCountries(selectedCountryList);
  
          // Update formData
          handleInputChange({
            target: {
              name: "geo",
              value: geo,
            },
          } as React.ChangeEvent<HTMLInputElement>);
        } catch (error) {
          console.error("Error fetching country data:", error);
        }
      };
  
      fetchCountries();
    }, [recordId, handleInputChange]);

  // Handle double-click on country to add/remove it from selectedCountries
  const handleDoubleClick = (country: Country) => {
    if (selectedCountries.some((item) => item.code === country.code)) {
      setSelectedCountries((prev) => prev.filter((item) => item.code !== country.code));
      setAvailableCountries((prev) => [...prev, country]);
    } else {
      setAvailableCountries((prev) => prev.filter((item) => item.code !== country.code));
      setSelectedCountries((prev) => [...prev, country]);
    }
  };

  // Update the geo field whenever selectedCountries changes
  useEffect(() => {
    const countryCodes = selectedCountries.map((country) => country.code).join(",");
    handleInputChange({
      target: {
        name: "geo",
        value: countryCodes,
      },
    } as React.ChangeEvent<HTMLInputElement>);
  }, [selectedCountries, handleInputChange]);

  const handleContinentSelection = (continent: string) => {
    const continentCountries = availableCountries.filter((country) => country.continent === continent);
    setSelectedCountries((prev) => [...prev, ...continentCountries]);
    setAvailableCountries((prev) =>
      prev.filter((country) => country.continent !== continent)
    );
  };

  const canEnableRegionTargeting = selectedCountries.length === 1;

  // Handle form submission and update the data in the database
  const handleUpdate = async () => {
    const updatedGeo = selectedCountries.map((country) => country.code).join(",");

    try {
      await axios.put(`https://bidvertiser-api.vercel.app/api/targeting/updateTargeting/${recordId}`, { geo: updatedGeo });
      alert("Geo Targeting updated successfully!");
      onUpdate();  // Call the parent callback to refresh the data
      handleClose();  // Close the popup
    } catch (error) {
      console.error("Error updating Geo Targeting:", error);
    }
  };

  return (
    <div className="MainGeo">
      <h1 className="Geoheading">GEO Targeting</h1>
      <p>
        BidVertiser allows you to display your ads within any geographic region you determine.
        <br />
        Select your desired countries and click <b>"Next"</b> and we will show your ads only to users located anywhere in the countries you select.
      </p>

      <div>
        <h1 className="Geoheading">Countries Targeting</h1>
        <div>
          <input
            type="checkbox"
            id="Continent"
            name="Continent"
            onChange={(e) => setIsContinentMode(e.target.checked)}
            style={{ marginRight: "10px" }}
          />
          <label style={{ fontSize: "12px" }} htmlFor="Continent">
            Quick selection by Continents
          </label>
        </div>

        {isContinentMode && (
          <div style={{ margin: "10px 0" }}>
            {continents.map((continent) => (
              <button
                key={continent}
                style={{
                  margin: "5px",
                  padding: "5px 10px",
                  border: "1px solid #ccc",
                  cursor: "pointer",
                }}
                onClick={() => handleContinentSelection(continent)}
              >
                Select All from {continent}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "left", gap: "20px", marginTop: "20px" }}>
          {/* Available Countries */}
          <div style={{ width: "300px", fontSize: "12px" }}>
            <h3 className="Geoheading">Available Countries</h3>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                border: "1px solid #ccc",
                height: "500px",
                overflowY: "scroll",
              }}
            >
              {availableCountries.map((country) => (
                <li
                  key={country.code}
                  style={{
                    padding: "2px 5px",
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onDoubleClick={() => handleDoubleClick(country)}
                >
                  <div style={{ display: "flex", alignItems: "center", fontStyle: "capitalize" }}>
                    <Flag code={country.code} style={{ width: "20px", height: "15px", marginRight: "10px" }} />
                    <span style={{ textTransform: "uppercase" }}>{country.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Selected Countries */}
          <div style={{ width: "300px", fontSize: "12px" }}>
            <h3 className="Geoheading">Selected Countries</h3>
            <ul
              style={{
                listStyle: "none",
                padding: "2px 5px",
                border: "1px solid #ccc",
                height: "500px",
                overflowY: "scroll",
              }}
            >
              {selectedCountries.map((country) => (
                <li
                  key={country.code}
                  style={{
                    padding: "0px",
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onDoubleClick={() => handleDoubleClick(country)}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Flag code={country.code} style={{ width: "20px", height: "15px", marginRight: "10px" }} />
                    <span style={{ textTransform: "uppercase" }}>{country.name}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Enable Region/State Targeting */}
        {selectedCountries.length > 0 && (
          <div style={{ marginTop: "20px" }}>
            <input
              type="checkbox"
              id="RegionTargeting"
              name="RegionTargeting"
              disabled={!canEnableRegionTargeting}
              checked={enableRegionTargeting}
              onChange={(e) => setEnableRegionTargeting(e.target.checked)}
              style={{ marginRight: "10px" }}
            />
            <label style={{ fontSize: "12px" }} htmlFor="RegionTargeting">
              Enable Region/State Targeting (Max 1 country selected)
            </label>
          </div>
        )}

        <button
          style={{ padding: "10px", backgroundColor: "#1A3F66", color: "white", borderRadius: "2px", marginTop: "20px" }}
          onClick={handleUpdate}
        >
          Update
        </button>
      </div>
    </div>
  );
};

export default GeoTargetingPopup;
